import { desktopBruecke, istGeraet } from './gleis';

/**
 * Wie ein Gerät heisst, solange niemand `device_name` gesetzt hat (Vorgabe des Nutzers,
 * 2026-10-08):
 *
 * - **App am iPad:** „iPad". Nicht der Name aus den iOS-Einstellungen — der ist, wo es ihn
 *   noch gibt, privat („iPad von …"), und ab iOS 16 liefert iOS ohnehin nur noch „iPad".
 * - **Desktop:** der Name des Rechners.
 * - **Browser:** die Marke („Chrome") und ein Zufallsname, einmal gewürfelt und gemerkt — sonst
 *   hiesse jeder Browser im Raum gleich und Relay könnte sie nicht auseinanderhalten.
 *
 * Den eigentlichen Namen setzt die Geräteverwaltung (MDM), die iPad-Seite, Relay per
 * `SET_SETTINGS` oder der Prüfer in den Einstellungen — **nie ein QR-Code**: ein Code gilt für
 * eine ganze Gruppe, der Name je Gerät (`server/relayEinstellungenQr.ts`).
 */

/** Ein kleiner Merkzettel — im Browser `localStorage`, im Test ein Objekt. */
export interface Merkzettel {
  lies(schluessel: string): string | null;
  schreib(schluessel: string, wert: string): void;
}

export type Umgebung =
  | { gleis: 'geraet' }
  | { gleis: 'desktop'; rechnername: () => Promise<string> }
  | { gleis: 'browser'; marke: string | null; merkzettel: Merkzettel; zufall: () => number };

const MERKER = 'geraet.zufallsname';

/** Kurz, unverwechselbar, in jeder der drei Sprachen lesbar. */
const NAMEN = [
  'Adler', 'Biber', 'Dachs', 'Elch', 'Falke', 'Fuchs', 'Igel', 'Kranich',
  'Luchs', 'Marder', 'Otter', 'Reiher', 'Specht', 'Wiesel', 'Zander', 'Lerche',
];

function zufallsname(merkzettel: Merkzettel, zufall: () => number): string {
  const gemerkt = merkzettel.lies(MERKER);
  if (gemerkt) return gemerkt;
  const name = NAMEN[Math.floor(zufall() * NAMEN.length) % NAMEN.length];
  const zahl = String(Math.floor(zufall() * 100) % 100).padStart(2, '0');
  const neu = name + '-' + zahl;
  try {
    merkzettel.schreib(MERKER, neu);
  } catch {
    // Ohne Ablage (privates Fenster) heisst der Browser eben nur in dieser Sitzung so.
  }
  return neu;
}

export async function vorgabeGeraetename(umgebung: Umgebung): Promise<string> {
  if (umgebung.gleis === 'geraet') return 'iPad';
  if (umgebung.gleis === 'desktop') {
    const name = await umgebung.rechnername().catch(() => '');
    return name.trim() || 'Desktop';
  }
  return (umgebung.marke ?? 'Browser') + ' ' + zufallsname(umgebung.merkzettel, umgebung.zufall);
}

/** Die Umgebung, in der die App gerade läuft — Klebstoff für `vorgabeGeraetename`. */
export function umgebungJetzt(): Umgebung {
  if (istGeraet()) return { gleis: 'geraet' };
  const bruecke = desktopBruecke();
  if (bruecke) return { gleis: 'desktop', rechnername: () => bruecke.rechnername() };
  const daten = (navigator as Navigator & { userAgentData?: { brands?: { brand: string }[] } }).userAgentData;
  const marke = daten?.brands?.find((b) => !/Not.?A.?Brand|Chromium/i.test(b.brand))?.brand ?? null;
  return {
    gleis: 'browser',
    marke,
    merkzettel: {
      lies: (k) => {
        try {
          return localStorage.getItem(k);
        } catch {
          return null;
        }
      },
      schreib: (k, v) => localStorage.setItem(k, v),
    },
    zufall: Math.random,
  };
}
